import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import API from '@/lib/api';
import Pagination, { usePagination } from '@/components/Pagination';
import { toast } from 'sonner';
import {
  Package, Camera, Warning, CheckCircle, Download, Trash, Clock, Archive,
  Calculator, MagnifyingGlass, FloppyDisk, X, ArrowCounterClockwise, FileXls,
  Barcode, Warning as WarningIcon, ShoppingCart, Truck, ClockCounterClockwise, Plus, Factory, FilePdf,
} from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/components/ConfirmDialog';
import SnelArtikelModal from '@/components/SnelArtikelModal';
import VariantPickerModal from '@/components/VariantPickerModal';
import { fmtDec, pf } from '@/lib/numFmt';
import BarcodeScanner from '@/components/BarcodeScanner';
import OntvangstGeschiedenis from '@/components/OntvangstGeschiedenis';
import { fetchPdfActions, openPdf } from '@/lib/pdfActions';

const fmt = (n) => `€ ${pf(n).toLocaleString('nl-BE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n) => pf(n).toFixed(2).replace('.', ',');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

export default function InventarisPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const confirm = useConfirm();

  const [tab, setTab] = useState('telling'); // 'telling' | 'momentopnames' | 'bestelsuggesties' | 'ontvangst'
  const [loading, setLoading] = useState(true);
  const [huidig, setHuidig] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [tellingen, setTellingen] = useState({}); // {art_id: value}
  const [saving, setSaving] = useState(false);
  const [scanMode, setScanMode] = useState(false);
  const [scanFeedback, setScanFeedback] = useState(''); // recent scan msg
  const scanInputRef = useRef(null);
  const [bestelsugg, setBestelsugg] = useState(null);
  const [historiekArt, setHistoriekArt] = useState(null); // {id, art_code, bewegingen}
  // Goederen ontvangen
  const [ontvangstRegels, setOntvangstRegels] = useState([]); // [{tmp_id, art_id, art_code, omschrijving, ean, aantal, aankoopprijs, voorraad_voor}]
  const [ontvangstScan, setOntvangstScan] = useState('');
  const [ontvangstAantal, setOntvangstAantal] = useState(1);
  const [ontvangstSugg, setOntvangstSugg] = useState([]); // autocomplete-suggesties
  const [ontvangstSuggIdx, setOntvangstSuggIdx] = useState(0);
  const [ontvangstPendingArt, setOntvangstPendingArt] = useState(null); // gekozen artikel, wacht op aantal-bevestiging
  const ontvangstAantalRef = useRef(null);
  const [ontvangstLev, setOntvangstLev] = useState('');
  const [ontvangstNota, setOntvangstNota] = useState('');
  const [ontvangstType, setOntvangstType] = useState('leverancier'); // 'leverancier' | 'productie' (eigen productie)
  const [ontvangstRefresh, setOntvangstRefresh] = useState(0); // bump → historiek herladen
  const [rapportBusy, setRapportBusy] = useState(false);
  const [rapportAlleenStock, setRapportAlleenStock] = useState(false);
  const inventarisExcel = async () => {
    setRapportBusy(true);
    try {
      const r = await API.get('/inventaris/rapport.xlsx', { params: { alleen_stock: rapportAlleenStock ? 1 : 0, categorie: cat || undefined }, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `Inventaris_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Excel gedownload', { description: 'Blad "Inventaris" (met formules) + blad "Per categorie"' });
    } catch (e) {
      toast.error('Excel maken mislukt', { description: e.response?.data?.detail || e.message });
    } finally { setRapportBusy(false); }
  };
  const inventarisRapport = async () => {
    setRapportBusy(true);
    try {
      const actions = await fetchPdfActions({
        endpoint: '/inventaris/rapport.pdf',
        params: { alleen_stock: rapportAlleenStock ? 1 : 0, categorie: cat || undefined },
        label: 'Inventarisrapport',
      });
      openPdf(actions);
    } catch (e) {
      toast.error('Rapport maken mislukt', { description: e.response?.data?.detail || e.message });
    } finally { setRapportBusy(false); }
  };
  const [ontvangstBusy, setOntvangstBusy] = useState(false);
  const [snelArtikel, setSnelArtikel] = useState(null); // { code, ean, aantal } — open modal/inline panel
  // Camera-barcodescanner (gsm/tablet): 'ontvangst' | 'telling' | null
  const [cameraScan, setCameraScan] = useState(null);
  const cameraDetectRef = useRef(() => false);
  const cameraLaatste = useRef({ code: '', t: 0 });
  const heeftCamera = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  const cameraOnDetect = useCallback((code, fmt) => cameraDetectRef.current(code, fmt), []);
  const cameraOnClose = useCallback(() => setCameraScan(null), []);
  // Inline-formulier state (volledig zichtbaar, geen modal-overlay)
  const [snelForm, setSnelForm] = useState({ art_code: '', ean: '', omschrijving: '', categorie: '', aankoopprijs: '', verkoopprijs: '', eenheid: 'st' });
  const [snelBusy, setSnelBusy] = useState(false);
  const snelCodeRef = useRef(null);
  const ontvangstScanRef = useRef(null);
  // Reset-stock modal
  const [resetOpen, setResetOpen] = useState(false);
  const [resetInput, setResetInput] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null); // {artikels_gereset, backup_naam}
  // Variant picker (voor ontvangst van artikels met varianten)
  const [variantPickerArt, setVariantPickerArt] = useState(null); // {artikel, preselectVariant?}

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      API.get('/inventaris/huidig'),
      API.get('/inventaris/momentopnames'),
      API.get('/inventaris/bestelsuggesties'),
    ]).then(([h, m, b]) => {
      setHuidig(h.data);
      setSnapshots(m.data || []);
      setBestelsugg(b.data);
    }).catch((e) => console.warn('Inventaris laden mislukt', e))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Barcode-scanner: luister naar scans (zowel USB-HID scanner als typ-invoer)
  useEffect(() => {
    if (!scanMode) return;
    scanInputRef.current?.focus();
  }, [scanMode]);

  const onScan = async (ean) => {
    const code = (ean || '').trim();
    if (!code) return;
    try {
      const r = await API.get(`/artikels/barcode/${encodeURIComponent(code)}`);
      const art = r.data;
      // Increment telling met 1 (of zet op 1 als nog niet ingegeven)
      setTellingen(p => {
        const huidige = parseFloat(p[art.id]) || 0;
        return { ...p, [art.id]: huidige + 1 };
      });
      setScanFeedback(`✓ ${art.art_code} — ${art.omschrijving || ''} · telling: ${(parseFloat(tellingen[art.id]) || 0) + 1}`);
      setQ(art.art_code); // scroll naar artikel in de lijst
      setTimeout(() => setScanFeedback(''), 3000);
    } catch (e) {
      setScanFeedback(`⚠ Geen artikel voor barcode "${code}"`);
      setTimeout(() => setScanFeedback(''), 3000);
    }
    if (scanInputRef.current) scanInputRef.current.value = '';
  };

  const openHistoriek = async (art) => {
    try {
      const r = await API.get(`/artikels/${art.id}/voorraad-bewegingen`, { params: { limit: 200 } });
      setHistoriekArt(r.data);
    } catch (e) {
      toast.error('Historiek laden mislukt', { description: e.response?.data?.detail || e.message });
    }
  };

  // Autocomplete: zoek live naar artikels terwijl gebruiker typt (debounced)
  useEffect(() => {
    if (tab !== 'ontvangst') return;
    const code = (ontvangstScan || '').trim();
    if (code.length < 2) {
      setOntvangstSugg([]);
      return;
    }
    const handle = setTimeout(() => {
      API.get('/artikels-zoek', { params: { q: code, limit: 8 } })
        .then(r => {
          setOntvangstSugg(r.data || []);
          setOntvangstSuggIdx(0);
        })
        .catch(() => setOntvangstSugg([]));
    }, 200);
    return () => clearTimeout(handle);
  }, [ontvangstScan, tab]);

  // ===== Goederen ontvangen handlers =====
  const ontvangstAddArt = (art, aantal = 1, variant = null) => {
    // variant: optioneel — bij artikels met varianten_actief, increment per variant
    const ck = variant?.combo_key || null;
    setOntvangstRegels(prev => {
      const idx = prev.findIndex(r => r.art_id === art.id && (r.combo_key || null) === ck);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], aantal: (parseFloat(next[idx].aantal) || 0) + (parseFloat(aantal) || 1) };
        return next;
      }
      const variantLabel = variant
        ? Object.values(variant.combo || {}).join(' / ')
        : '';
      return [
        {
          tmp_id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          art_id: art.id,
          art_code: variant?.art_code || art.art_code,
          omschrijving: art.omschrijving + (variantLabel ? ` · ${variantLabel}` : ''),
          ean: variant?.ean || art.ean || '',
          aantal: parseFloat(aantal) || 1,
          aankoopprijs: parseFloat(art.aankoopprijs) || 0,
          voorraad_voor: variant ? (parseFloat(variant.voorraad) || 0) : (parseFloat(art.voorraad) || 0),
          combo_key: ck,
          variant_label: variantLabel,
        },
        ...prev,
      ];
    });
    setOntvangstScan('');
    setOntvangstAantal(1);
    setOntvangstSugg([]);
    setOntvangstSuggIdx(0);
    setOntvangstPendingArt(null);
    setVariantPickerArt(null);
    setTimeout(() => ontvangstScanRef.current?.focus(), 50);
  };

  // Stap 1: artikel kiezen (vanuit suggestie of exacte barcode-lookup)
  // Als artikel varianten heeft → open variant-picker (eventueel met preselect uit barcode-match)
  const ontvangstSelectArt = (art, preselectVariant = null) => {
    const heeftVarianten = !!art.varianten_actief && (art.varianten || []).length > 0;
    if (heeftVarianten) {
      setVariantPickerArt({ artikel: art, preselectVariant });
      setOntvangstScan('');
      setOntvangstSugg([]);
      setOntvangstSuggIdx(0);
      return;
    }
    setOntvangstPendingArt(art);
    setOntvangstScan('');
    setOntvangstSugg([]);
    setOntvangstSuggIdx(0);
    setOntvangstAantal('');
    // Focus aantal-veld + selecteer inhoud zodat tikken meteen overschrijft
    setTimeout(() => {
      ontvangstAantalRef.current?.focus();
      ontvangstAantalRef.current?.select();
    }, 50);
  };

  // Stap 2: bevestig aantal en voeg toe
  const ontvangstConfirmAantal = () => {
    if (!ontvangstPendingArt) return;
    const aantal = pf(ontvangstAantal) || 1;
    ontvangstAddArt(ontvangstPendingArt, aantal);
    toast.success(`${ontvangstPendingArt.art_code} toegevoegd`, { description: `${aantal} stuks` });
  };

  const ontvangstScanSubmit = async () => {
    // Als er al een artikel klaarstaat → ENTER hier ook bevestigt het aantal
    if (ontvangstPendingArt) {
      ontvangstConfirmAantal();
      return;
    }
    // Als er suggesties zichtbaar zijn → kies de gehighlighte (focus naar aantal)
    if (ontvangstSugg.length > 0) {
      const art = ontvangstSugg[ontvangstSuggIdx] || ontvangstSugg[0];
      ontvangstSelectArt(art);
      return;
    }
    const code = (ontvangstScan || '').trim();
    if (!code) return;
    try {
      const r = await API.get(`/artikels/barcode/${encodeURIComponent(code)}`);
      // Als de scan een variant-EAN matcht → preselect die variant in de picker
      const preselect = r.data.variant || null;
      ontvangstSelectArt(r.data, preselect);
    } catch (e) {
      if (e.response?.status === 404) {
        const isEan = /^\d{8,14}$/.test(code);
        setSnelArtikel({
          code: isEan ? '' : code,
          ean: isEan ? code : '',
          aantal: parseFloat(ontvangstAantal) || 1,
        });
      } else {
        toast.error('Lookup mislukt', { description: e.response?.data?.detail || e.message });
      }
    }
  };

  // Camera-scan → zelfde flow als USB-scanner. Ontvangst: artikel kiezen en camera sluiten (aantal typen).
  // Telling: camera blijft open, elke scan = +1 (zelfde code max. 1× per 2,5 s).
  cameraDetectRef.current = (code) => {
    const c = (code || '').trim();
    if (!c) return true;
    if (cameraScan === 'telling') {
      const nu = Date.now();
      if (cameraLaatste.current.code === c && nu - cameraLaatste.current.t < 2500) return true;
      cameraLaatste.current = { code: c, t: nu };
      onScan(c);
      return true;
    }
    API.get(`/artikels/barcode/${encodeURIComponent(c)}`).then(r => {
      ontvangstSelectArt(r.data, r.data.variant || null);
      toast.success(`${r.data.art_code} gescand`, { description: 'Typ het aantal en druk ENTER' });
    }).catch(e => {
      if (e.response?.status === 404) {
        setOntvangstScan(c);
        toast.warning(`Onbekende barcode ${c}`, { description: 'Maak hieronder een nieuw artikel aan' });
        setTimeout(() => ontvangstScanRef.current?.focus(), 100);
      } else {
        toast.error('Lookup mislukt', { description: e.response?.data?.detail || e.message });
      }
    });
    return false;
  };

  const ontvangstUpdRegel = (tmp_id, veld, waarde) => {
    setOntvangstRegels(prev => prev.map(r => r.tmp_id === tmp_id ? { ...r, [veld]: waarde } : r));
  };
  const ontvangstDelRegel = (tmp_id) => {
    setOntvangstRegels(prev => prev.filter(r => r.tmp_id !== tmp_id));
  };

  const ontvangstVerwerken = async () => {
    if (ontvangstRegels.length === 0) { toast.info('Geen regels om te verwerken'); return; }
    const ok = await confirm({
      title: 'Goederen verwerken',
      subtitle: `${ontvangstRegels.length} artikel(s) optellen bij voorraad`,
      message: 'Voor elk artikel wordt het ingevulde aantal opgeteld bij de huidige voorraad. Deze actie wordt geregistreerd in de stock-historiek (bron: "ontvangst").',
      tone: 'info',
      confirmText: 'Verwerken',
    });
    if (!ok) return;
    setOntvangstBusy(true);
    try {
      const regels = ontvangstRegels.map(r => ({
        art_id: r.art_id,
        art_code: r.art_code,
        omschrijving: r.omschrijving,
        ean: r.ean || '',
        variant_label: r.variant_label || '',
        aantal: pf(r.aantal),
        aankoopprijs: r.aankoopprijs !== '' && r.aankoopprijs !== null ? pf(r.aankoopprijs) : null,
        combo_key: r.combo_key || null,
      })).filter(r => r.aantal > 0);
      const res = await API.post('/inventaris/ontvangst', {
        regels,
        type: ontvangstType,
        leverancier: ontvangstType === 'productie' ? '' : ontvangstLev,
        nota: ontvangstNota,
      });
      toast.success(`${res.data.verwerkt} artikel(s) verwerkt`, {
        description: `${res.data.totaal_aantal} stuks · ${fmt(res.data.totaal_waarde)} totale waarde`,
      });
      setOntvangstRegels([]);
      setOntvangstLev('');
      setOntvangstNota('');
      setOntvangstType('leverancier');
      setOntvangstRefresh(n => n + 1);
      load();
    } catch (e) {
      toast.error('Verwerken mislukt', { description: e.response?.data?.detail || e.message });
    } finally {
      setOntvangstBusy(false);
    }
  };

  const snelArtikelOpslaan = async (form) => {
    try {
      const r = await API.post('/artikels', {
        art_code: form.art_code.trim(),
        ean: form.ean.trim() || null,
        omschrijving: form.omschrijving.trim(),
        categorie: form.categorie || '',
        aankoopprijs: parseFloat(form.aankoopprijs) || 0,
        verkoopprijs_basic: parseFloat(form.verkoopprijs) || 0,
        eenheid: form.eenheid || 'st',
        voorraad: 0,
        min_voorraad: 0,
      });
      const aantal = parseFloat(snelArtikel.aantal) || 1;
      ontvangstAddArt({ ...r.data, voorraad: 0 }, aantal);
      toast.success(`Nieuw artikel "${r.data.art_code}" aangemaakt`, {
        description: `${aantal} stuks toegevoegd aan ontvangst`,
      });
      setSnelArtikel(null);
    } catch (e) {
      toast.error('Aanmaken mislukt', { description: e.response?.data?.detail || e.message });
    }
  };

  // Inline panel — synchroniseer formulier wanneer snelArtikel verandert + auto-focus
  useEffect(() => {
    if (snelArtikel) {
      setSnelForm({
        art_code: snelArtikel.code || '',
        ean: snelArtikel.ean || '',
        omschrijving: '',
        categorie: '',
        aankoopprijs: '',
        verkoopprijs: '',
        eenheid: 'st',
      });
      setTimeout(() => snelCodeRef.current?.focus(), 100);
    }
  }, [snelArtikel]);

  const submitSnelInline = async () => {
    if (!snelForm.art_code.trim() || !snelForm.omschrijving.trim()) return;
    setSnelBusy(true);
    try {
      await snelArtikelOpslaan(snelForm);
    } finally {
      setSnelBusy(false);
    }
  };


  const maakBestelbon = async (groep) => {
    if (!groep.artikels?.length) return;
    const ok = await confirm({
      title: 'Bestelbon aanmaken',
      subtitle: `Bij ${groep.leverancier_naam} — ${groep.artikels.length} artikel(s)`,
      message: `Er wordt een aankoopbestelling aangemaakt met ${groep.artikels.length} artikel(s) voor totaal ${fmt(groep.totaal_waarde)} (excl. BTW).\n\nDe aantallen zijn berekend om elk artikel terug op zijn maximum-voorraad te brengen.`,
      tone: 'info',
      confirmText: 'Bestelbon aanmaken',
    });
    if (!ok) return;
    try {
      const regels = groep.artikels.map(a => ({
        art_id: a.id,
        art_code: a.art_code,
        ean: a.ean || '',
        omschrijving: a.omschrijving,
        aantal: a.bij_te_bestellen,
        prijs: a.aankoopprijs || 0,
      }));
      const r = await API.post('/voorraad/bestel-bij-leverancier', {
        leverancier: groep.leverancier_naam,
        leverancier_id: groep.leverancier_id,
        regels,
      });
      toast.success(`Bestelbon ${r.data.id} aangemaakt`, {
        description: `${regels.length} regel(s) — totaal ${fmt(r.data.totaal)}`,
        action: {
          label: 'Bekijken',
          onClick: () => { window.location.href = '/aankoopbestellingen'; },
        },
      });
      load();
    } catch (e) {
      toast.error('Bestelbon mislukt', { description: e.response?.data?.detail || e.message });
    }
  };

  const categorieen = useMemo(() => {
    if (!huidig) return [];
    const s = new Set();
    huidig.items.forEach(i => i.categorie && s.add(i.categorie));
    return [...s].sort();
  }, [huidig]);

  const gefilterd = useMemo(() => {
    if (!huidig) return [];
    return huidig.items.filter(i =>
      (!cat || i.categorie === cat) &&
      (!q || `${i.art_code} ${i.omschrijving} ${i.ean}`.toLowerCase().includes(q.toLowerCase()))
    );
  }, [huidig, q, cat]);

  const { pageSize, setPageSize, curPage, setPage, paged } = usePagination(gefilterd, 'inventaris_telling');

  // Totale waarde van nieuwe telling (ook voor artikels zonder telling → gebruik huidige stock als fallback)
  const nieuweWaarde = useMemo(() => {
    if (!huidig) return { ap: 0, vp: 0 };
    let ap = 0, vp = 0;
    huidig.items.forEach(i => {
      const v = tellingen[i.id] !== undefined ? pf(tellingen[i.id]) : i.voorraad;
      ap += (isNaN(v) ? 0 : v) * (i.aankoopprijs || 0);
      vp += (isNaN(v) ? 0 : v) * (i.verkoopprijs_basic || 0);
    });
    return { ap, vp };
  }, [huidig, tellingen]);

  const gewijzigd = Object.keys(tellingen).length;

  // Browser-waarschuwing wanneer er onbewaarde tellingen zijn en de gebruiker wegnavigeert / tab sluit
  useEffect(() => {
    if (gewijzigd === 0) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = 'Je hebt onbewaarde voorraad-tellingen. Wil je echt weggaan?';
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [gewijzigd]);

  const saveTellingen = async () => {
    if (gewijzigd === 0) { toast.info('Geen wijzigingen om op te slaan'); return; }
    setSaving(true);
    try {
      const updates = Object.entries(tellingen).map(([id, v]) => ({ id, voorraad: parseFloat(v) || 0 }));
      const r = await API.post('/inventaris/bulk-stock', { updates });
      toast.success(`${r.data.bijgewerkt} artikels bijgewerkt`);
      setTellingen({});
      load();
    } catch (e) {
      toast.error('Opslaan mislukt', { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const maakMomentopname = async () => {
    const naam = window.prompt('Naam voor deze momentopname:', `Inventaris ${new Date().getFullYear()} — ${new Date().toLocaleDateString('nl-BE')}`);
    if (!naam) return;
    try {
      const r = await API.post('/inventaris/momentopname', { naam });
      toast.success(`Momentopname "${r.data.naam}" opgeslagen`, {
        description: `Totale waarde (aankoop): ${fmt(r.data.totaal_waarde_aankoop)}`,
      });
      load();
      setTab('momentopnames');
    } catch (e) {
      toast.error('Momentopname mislukt', { description: e.response?.data?.detail || e.message });
    }
  };

  const resetStock = () => {
    if (!isAdmin) { toast.error('Enkel admin kan stock resetten'); return; }
    setResetInput('');
    setResetResult(null);
    setResetOpen(true);
  };

  const doResetStock = async () => {
    if (resetInput !== 'RESET') return;
    setResetBusy(true);
    try {
      const r = await API.post('/inventaris/reset-stock', { bevestiging: 'RESET-STOCK' });
      setResetResult({
        artikels_gereset: r.data.artikels_gereset,
        backup_naam: r.data.backup_naam,
      });
      load();
    } catch (e) {
      toast.error('Reset mislukt', { description: e.response?.data?.detail || e.message });
      setResetBusy(false);
    } finally {
      setResetBusy(false);
    }
  };

  const downloadExcel = async (sid) => {
    try {
      const r = await API.get(`/inventaris/momentopname/${sid}/excel`, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventaris_${sid}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Download mislukt', { description: e.response?.data?.detail || e.message });
    }
  };

  const delSnapshot = async (sid, naam) => {
    if (!isAdmin) return;
    const ok = await confirm({
      title: 'Momentopname verwijderen',
      subtitle: 'Definitief verwijderen — niet omkeerbaar.',
      message: `De momentopname "${naam}" wordt definitief verwijderd. Andere snapshots blijven onaangetast.`,
      tone: 'danger',
      confirmText: 'Verwijderen',
    });
    if (!ok) return;
    try {
      await API.delete(`/inventaris/momentopname/${sid}`);
      toast.success('Momentopname verwijderd');
      load();
    } catch (e) {
      toast.error('Verwijderen mislukt', { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div className={`p-6 max-w-[1500px] mx-auto ${gewijzigd > 0 ? 'pb-32' : ''}`} data-testid="inventaris-page">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Package size={24} weight="duotone" className="text-[#002FA7]" />
        <h1 className="text-2xl font-bold text-gray-900">Inventaris & voorraadbeheer</h1>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
            <input type="checkbox" checked={rapportAlleenStock} onChange={e => setRapportAlleenStock(e.target.checked)} className="w-3.5 h-3.5 accent-[#002FA7]" data-testid="inventaris-rapport-alleen-stock" />
            enkel artikels met stock
          </label>
          <button type="button" onClick={inventarisRapport} disabled={rapportBusy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold rounded-lg bg-[#002FA7] text-white hover:bg-[#00247f] disabled:opacity-50 shadow-sm"
            title={`Volledig inventarisrapport (pdf): alle artikels${cat ? ` in categorie ${cat}` : ''} met stock, aankoop- en verkoopprijs, waardes en subtotalen per categorie`}
            data-testid="inventaris-rapport-btn">
            <FilePdf size={16} weight="bold" /> {rapportBusy ? 'Bezig…' : `Volledig rapport PDF${cat ? ` · ${cat}` : ''}`}
          </button>
          <button type="button" onClick={inventarisExcel} disabled={rapportBusy}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-bold rounded-lg bg-[#1D6F42] text-white hover:bg-[#155a35] disabled:opacity-50 shadow-sm"
            title="Hetzelfde rapport als Excel voor de boekhouder: blad Inventaris (alle artikels, formules, subtotalen, filter) + blad Per categorie"
            data-testid="inventaris-rapport-xlsx-btn">
            <Download size={16} weight="bold" /> Excel
          </button>
        </div>
      </div>

      {/* KPI-strook */}
      {huidig && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-white border border-gray-200 p-3 rounded">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Artikels</div>
            <div className="text-2xl font-bold text-gray-900">{huidig.totaal_artikels}</div>
          </div>
          <div className="bg-white border border-gray-200 p-3 rounded">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Huidige waarde (aankoop)</div>
            <div className="text-2xl font-bold text-[#002FA7]">{fmt(huidig.totaal_waarde_aankoop)}</div>
          </div>
          <div className="bg-white border border-gray-200 p-3 rounded">
            <div className="text-[11px] uppercase tracking-wider text-gray-500">Huidige waarde (verkoop)</div>
            <div className="text-2xl font-bold text-[#059669]">{fmt(huidig.totaal_waarde_verkoop)}</div>
          </div>
          {tab === 'ontvangst' && ontvangstRegels.length > 0 && (() => {
            const extraAp = ontvangstRegels.reduce((s, r) => s + pf(r.aantal) * pf(r.aankoopprijs), 0);
            const extraVp = ontvangstRegels.reduce((s, r) => s + pf(r.aantal) * pf((huidig.items || []).find(i => i.id === r.art_id)?.verkoopprijs_basic), 0);
            return (
              <div className="bg-green-50 border-2 border-green-300 p-3 rounded" data-testid="kpi-na-ontvangst">
                <div className="text-[11px] uppercase tracking-wider text-green-700">Waarde na verwerken ontvangst</div>
                <div className="text-2xl font-bold text-green-900">{fmt(pf(huidig.totaal_waarde_aankoop) + extraAp)}</div>
                <div className="text-[11px] text-green-700">+{fmt(extraAp)} aankoop · verkoop {fmt(pf(huidig.totaal_waarde_verkoop) + extraVp)} — nog niet verwerkt</div>
              </div>
            );
          })()}
          {gewijzigd > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 p-3 rounded">
              <div className="text-[11px] uppercase tracking-wider text-amber-700">Nieuwe waarde na telling</div>
              <div className="text-2xl font-bold text-amber-900">{fmt(nieuweWaarde.ap)}</div>
              <div className="text-[11px] text-amber-700">{gewijzigd} wijzigingen</div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 mb-4">
        {[
          { k: 'telling', l: 'Telling & bulk-invoer', icon: <Calculator size={14} /> },
          { k: 'ontvangst', l: `Goederen ontvangen${ontvangstRegels.length ? ` (${ontvangstRegels.length})` : ''}`, icon: <Truck size={14} /> },
          { k: 'bestelsuggesties', l: `Bestelsuggesties${bestelsugg?.totaal_tekort_artikels ? ` (${bestelsugg.totaal_tekort_artikels})` : ''}`, icon: <ShoppingCart size={14} />, alert: (bestelsugg?.totaal_tekort_artikels || 0) > 0 },
          { k: 'momentopnames', l: `Momentopnames (${snapshots.length})`, icon: <Archive size={14} /> },
        ].map(t => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-semibold flex items-center gap-1.5 border-b-2 ${tab === t.k ? 'text-[#002FA7] border-[#002FA7]' : 'text-gray-600 border-transparent hover:text-gray-800'} ${t.alert ? 'text-red-600' : ''}`}
            data-testid={`inventaris-tab-${t.k}`}
          >
            {t.icon} {t.l}
            {t.alert && <span className="ml-1 w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
          </button>
        ))}
      </div>

      {tab === 'telling' && huidig && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm" data-testid="telling-panel">
          {/* Kop — zelfde stijl als Goederen ontvangen */}
          <div className="bg-[#002FA7] text-white rounded-t-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                <Calculator size={20} weight="duotone" />
              </div>
              <div className="min-w-0">
                <div className="font-bold text-sm leading-tight">Telling & bulk-invoer</div>
                <div className="text-[11px] text-white/70 leading-tight">Tel uw stock per artikel of scan barcodes — elke scan telt +1</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {gewijzigd > 0 && (
                <>
                  <button
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full bg-[#22C55E] text-white hover:bg-[#1EAF54] disabled:opacity-50 transition-colors"
                    disabled={saving}
                    onClick={saveTellingen}
                    data-testid="inventaris-save"
                  >
                    <FloppyDisk size={14} weight="bold" /> Bewaar telling ({gewijzigd})
                  </button>
                  <button
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-white/30 text-white hover:bg-white hover:text-[#002FA7] transition-colors"
                    onClick={() => setTellingen({})}
                    data-testid="inventaris-reset-telling"
                  >
                    <X size={14} /> Annuleer
                  </button>
                </>
              )}
              <button
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${scanMode ? 'bg-white text-[#002FA7]' : 'border border-white/30 text-white hover:bg-white hover:text-[#002FA7]'}`}
                onClick={() => setScanMode(v => !v)}
                data-testid="inventaris-scan-toggle"
                title="Barcode-scan-modus: scan EANs met USB-scanner, camera of typ ze in — telling wordt automatisch +1 voor elk gescand artikel"
              >
                <Barcode size={14} weight="bold" /> {scanMode ? 'Scan-modus AAN' : 'Scan-modus'}
              </button>
              <button
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-white/30 text-white hover:bg-white hover:text-[#002FA7] transition-colors"
                onClick={maakMomentopname}
                data-testid="inventaris-momentopname"
                title="Sla huidige stock-stand op als archief (voor boekhouder)"
              >
                <Camera size={14} /> Momentopname
              </button>
              {isAdmin && (
                <button
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-red-300/60 text-red-100 hover:bg-red-600 hover:border-red-600 hover:text-white transition-colors"
                  onClick={resetStock}
                  data-testid="inventaris-reset-stock"
                  title="Zet alle voorraden op 0 (voor jaarlijkse heropname) — back-up wordt automatisch gemaakt"
                >
                  <ArrowCounterClockwise size={14} /> Reset alle stock
                </button>
              )}
            </div>
          </div>

          {/* Zoek- & scanbalk */}
          <div className="p-4 space-y-3 border-b border-gray-100">
            <div className="flex items-stretch gap-3 flex-wrap md:flex-nowrap">
              <div className="flex-1 relative min-w-[220px]">
                <MagnifyingGlass size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  className="w-full h-12 pl-11 pr-3 text-base bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/15 focus:outline-none transition-colors"
                  placeholder="Zoek op code, omschrijving of EAN…"
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  data-testid="inventaris-zoek"
                />
              </div>
              <select
                className="h-12 px-3 text-sm bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-[#002FA7] focus:outline-none w-full md:w-56 shrink-0"
                value={cat}
                onChange={e => setCat(e.target.value)}
                data-testid="inventaris-cat"
              >
                <option value="">Alle categorieën</option>
                {categorieen.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Scan-input — enkel zichtbaar in scan-modus */}
            {scanMode && (
              <div className="flex items-stretch gap-3 flex-wrap md:flex-nowrap" data-testid="scan-bar">
                <div className="flex-1 relative min-w-[220px]">
                  <Barcode size={20} weight="bold" className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#002FA7] pointer-events-none" />
                  <input
                    ref={scanInputRef}
                    type="text"
                    className={`w-full h-12 pl-11 ${heeftCamera ? 'pr-12' : 'pr-3'} text-base font-mono bg-white border-2 border-[#002FA7] rounded-lg focus:ring-2 focus:ring-[#002FA7]/15 focus:outline-none`}
                    placeholder="Scan of typ barcode/EAN en druk ENTER (cursor staat hier klaar)…"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        onScan(e.target.value);
                      }
                    }}
                    autoFocus
                    data-testid="scan-input"
                  />
                  {heeftCamera && (
                    <button
                      type="button"
                      onClick={() => setCameraScan('telling')}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-md inline-flex items-center justify-center text-[#002FA7] hover:bg-[#002FA7]/10 transition-colors"
                      title="Scan met de camera (gsm/tablet) — blijft tellen tot u sluit"
                      data-testid="telling-camera-btn"
                    >
                      <Camera size={20} weight="duotone" />
                    </button>
                  )}
                </div>
                {scanFeedback && (
                  <div className={`h-12 px-4 inline-flex items-center text-sm font-bold rounded-lg border ${scanFeedback.startsWith('✓') ? 'text-[#047857] bg-green-50 border-green-200' : 'text-[#DC2626] bg-red-50 border-red-200'}`} data-testid="scan-feedback">
                    {scanFeedback}
                  </div>
                )}
                <button
                  className="h-12 w-12 inline-flex items-center justify-center rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-100 hover:text-gray-900 shrink-0"
                  onClick={() => setScanMode(false)}
                  title="Scan-modus sluiten"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Tabel */}
          <div className="overflow-x-auto">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>Art. code</th>
                  <th>EAN</th>
                  <th>Omschrijving</th>
                  <th>Categorie</th>
                  <th className="right">Huidige stock</th>
                  <th className="right">Geteld</th>
                  <th className="right">Verschil</th>
                  <th className="right">Aankoop</th>
                  <th className="right">Waarde</th>
                  <th className="right" style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {paged.map(i => {
                  const geteld = tellingen[i.id];
                  const nieuweStock = geteld !== undefined ? pf(geteld) : i.voorraad;
                  const verschil = nieuweStock - i.voorraad;
                  const gewijzigdeRow = geteld !== undefined;
                  return (
                    <tr key={i.id} className={gewijzigdeRow ? 'bg-amber-50' : ''} data-testid={`inv-row-${i.id}`}>
                      <td className="font-mono font-bold">{i.art_code}</td>
                      <td className="font-mono text-[10px] text-gray-500">{i.ean || '-'}</td>
                      <td>{i.omschrijving}</td>
                      <td className="text-gray-500">{i.categorie || '-'}</td>
                      <td className="right">{fmtNum(i.voorraad)}</td>
                      <td className="right">
                        <input
                          type="text"
                          inputMode="decimal"
                          className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-xs font-bold focus:border-[#002FA7] focus:outline-none"
                          placeholder={fmtNum(i.voorraad)}
                          value={fmtDec(geteld ?? '')}
                          onChange={e => setTellingen(p => ({ ...p, [i.id]: e.target.value.replace(',', '.') }))}
                          data-testid={`inv-inp-${i.id}`}
                        />
                      </td>
                      <td className={`right font-bold ${verschil > 0 ? 'text-[#059669]' : verschil < 0 ? 'text-[#DC2626]' : 'text-gray-400'}`}>
                        {gewijzigdeRow ? (verschil > 0 ? `+${fmtNum(verschil)}` : fmtNum(verschil)) : '-'}
                      </td>
                      <td className="right text-gray-500">{fmt(i.aankoopprijs)}</td>
                      <td className="right font-bold">{fmt(nieuweStock * i.aankoopprijs)}</td>
                      <td className="right">
                        <button
                          onClick={() => openHistoriek(i)}
                          className="p-1 text-gray-400 hover:text-[#002FA7] hover:bg-blue-50 rounded transition-colors"
                          title="Stock-historiek bekijken"
                          data-testid={`inv-hist-${i.id}`}
                        >
                          <ClockCounterClockwise size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {paged.length === 0 && <tr><td colSpan="10" className="text-center text-gray-400 py-8">Geen artikels gevonden</td></tr>}
              </tbody>
            </table>
          </div>
          <Pagination total={gefilterd.length} pageSize={pageSize} setPageSize={setPageSize} curPage={curPage} setPage={setPage} />
        </div>
      )}

      {tab === 'ontvangst' && (
        <div className="space-y-4">
          {/* Scan/typ-bar */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-visible" data-testid="ontvangst-panel">
            {/* Kop */}
            <div className="bg-[#002FA7] text-white rounded-t-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
                  <Truck size={20} weight="duotone" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm leading-tight">Goederen ontvangen</div>
                  <div className="text-[11px] text-white/60 leading-tight">Scan of typ artikelcode, EAN of omschrijving — stock wordt bijgeboekt</div>
                </div>
              </div>
              <button
                onClick={() => setSnelArtikel({ code: '', ean: '', aantal: parseFloat(ontvangstAantal) || 1 })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border border-white/30 text-white hover:bg-white hover:text-[#002FA7] transition-colors"
                data-testid="ontvangst-nieuw-artikel"
                title="Maak een nieuw artikel aan en voeg het direct toe aan de ontvangst"
              >
                <Plus size={14} weight="bold" /> Nieuw artikel
              </button>
            </div>

            {/* Scanbalk: zoekveld + aantal-stepper + actie, alles op 1 hoogte */}
            <div className="p-4">
            <div className="flex items-stretch gap-3 flex-wrap md:flex-nowrap relative">
              <div className="flex-1 min-w-[220px] relative">
                <Barcode size={20} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  ref={ontvangstScanRef}
                  type="text"
                  value={ontvangstScan}
                  onChange={e => setOntvangstScan(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); ontvangstScanSubmit(); }
                    else if (e.key === 'ArrowDown' && ontvangstSugg.length > 0) {
                      e.preventDefault();
                      setOntvangstSuggIdx(i => Math.min(i + 1, ontvangstSugg.length - 1));
                    }
                    else if (e.key === 'ArrowUp' && ontvangstSugg.length > 0) {
                      e.preventDefault();
                      setOntvangstSuggIdx(i => Math.max(i - 1, 0));
                    }
                    else if (e.key === 'Escape') {
                      setOntvangstSugg([]);
                    }
                  }}
                  placeholder="Scan of typ artikelcode, EAN of (deel van) omschrijving…"
                  autoFocus
                  className={`w-full h-12 pl-11 ${heeftCamera ? 'pr-12' : 'pr-3'} text-base font-mono bg-gray-50 border border-gray-300 rounded-lg focus:bg-white focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/15 focus:outline-none transition-colors`}
                  data-testid="ontvangst-scan-input"
                  autoComplete="off"
                />
                {heeftCamera && (
                  <button
                    type="button"
                    onClick={() => setCameraScan('ontvangst')}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9 rounded-md inline-flex items-center justify-center text-[#002FA7] hover:bg-[#002FA7]/10 transition-colors"
                    title="Scan met de camera (gsm/tablet)"
                    data-testid="ontvangst-camera-btn"
                  >
                    <Camera size={20} weight="duotone" />
                  </button>
                )}
                {/* Autocomplete dropdown */}
                {ontvangstSugg.length > 0 && (
                  <div className="absolute z-30 left-0 right-0 mt-1.5 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-auto" data-testid="ontvangst-sugg">
                    {ontvangstSugg.map((s, i) => (
                      <button
                        key={s.id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); setOntvangstSuggIdx(i); ontvangstSelectArt(s); }}
                        onMouseEnter={() => setOntvangstSuggIdx(i)}
                        className={`w-full text-left px-3 py-2 flex items-center gap-3 border-b border-gray-100 last:border-0 ${i === ontvangstSuggIdx ? 'bg-[#002FA7]/5' : 'hover:bg-gray-50'}`}
                        data-testid={`ontvangst-sugg-${i}`}
                      >
                        <div className="font-mono font-bold text-[#002FA7] text-sm shrink-0 w-28 truncate">
                          {s.art_code}
                          {s.varianten_actief && (s.varianten || []).length > 0 && (
                            <span className="ml-1 text-[8px] bg-blue-100 text-blue-700 px-1 rounded">{(s.varianten || []).length} VAR</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-900 truncate">{s.omschrijving}</div>
                          <div className="text-[10px] text-gray-500 font-mono">{s.ean ? `EAN ${s.ean}` : ''}{s.categorie ? ` · ${s.categorie}` : ''}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-[10px] uppercase text-gray-400">stock</div>
                          <div className="text-sm font-bold text-gray-700">{Number(s.voorraad || 0).toFixed(2).replace('.', ',')}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {/* Geen-resultaat banner — toon altijd link naar nieuw-artikel-modal */}
                {ontvangstSugg.length === 0 && (ontvangstScan || '').trim().length >= 2 && !ontvangstPendingArt && (
                  <div className="absolute z-30 left-0 right-0 mt-1.5 bg-white border border-amber-300 rounded-lg shadow-xl p-3" data-testid="ontvangst-geen-resultaat">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Warning size={20} weight="duotone" className="text-amber-600 shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-gray-900">
                            Geen artikel gevonden voor "{ontvangstScan}"
                          </div>
                          <div className="text-[11px] text-gray-500">
                            Maak een nieuw artikel aan met deze {/^\d{8,14}$/.test(ontvangstScan.trim()) ? 'EAN' : 'code'} en voeg het direct toe.
                          </div>
                        </div>
                      </div>
                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const code = ontvangstScan.trim();
                          const isEan = /^\d{8,14}$/.test(code);
                          setSnelArtikel({
                            code: isEan ? '' : code,
                            ean: isEan ? code : '',
                            aantal: parseFloat(ontvangstAantal) || 1,
                          });
                        }}
                        className="btn btn-sm inline-flex items-center gap-1.5 bg-amber-500 text-white hover:bg-amber-600 shrink-0"
                        data-testid="ontvangst-maak-nieuw"
                      >
                        <Plus size={14} weight="bold" /> Nieuw artikel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Aantal-stepper */}
              <div className={`flex items-stretch h-12 rounded-lg border bg-white overflow-hidden shrink-0 transition-colors ${ontvangstPendingArt ? 'border-[#059669] ring-2 ring-green-200' : 'border-gray-300'}`} data-testid="ontvangst-aantal-stepper">
                <button
                  type="button"
                  onClick={() => setOntvangstAantal(String(Math.max(0, (parseFloat(ontvangstAantal) || 0) - 1)))}
                  className="w-10 text-gray-500 hover:bg-gray-100 hover:text-gray-900 text-lg font-bold select-none"
                  title="Aantal −1"
                  data-testid="ontvangst-aantal-min"
                  tabIndex={-1}
                >−</button>
                <div className="relative flex flex-col justify-center border-x border-gray-200">
                  <span className="absolute top-1 left-0 right-0 text-center text-[9px] font-semibold uppercase tracking-wider text-gray-400 pointer-events-none">Aantal</span>
                  <input
                    ref={ontvangstAantalRef}
                    type="text"
                    inputMode="decimal"
                    value={fmtDec(ontvangstAantal)}
                    onChange={e => setOntvangstAantal(e.target.value.replace(',', '.'))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (ontvangstPendingArt) ontvangstConfirmAantal();
                        else ontvangstScanSubmit();
                      } else if (e.key === 'Escape' && ontvangstPendingArt) {
                        setOntvangstPendingArt(null);
                        setOntvangstAantal(1);
                        setTimeout(() => ontvangstScanRef.current?.focus(), 50);
                      }
                    }}
                    placeholder="1"
                    className={`w-20 h-full pt-3 text-center text-lg font-bold bg-transparent focus:outline-none ${ontvangstPendingArt ? 'text-[#047857]' : 'text-gray-900'}`}
                    data-testid="ontvangst-aantal-input"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setOntvangstAantal(String((parseFloat(ontvangstAantal) || 0) + 1))}
                  className="w-10 text-gray-500 hover:bg-gray-100 hover:text-gray-900 text-lg font-bold select-none"
                  title="Aantal +1"
                  data-testid="ontvangst-aantal-plus"
                  tabIndex={-1}
                >+</button>
              </div>

              <button
                onClick={() => { if (ontvangstPendingArt) ontvangstConfirmAantal(); else ontvangstScanSubmit(); }}
                className={`h-12 px-5 text-sm font-bold inline-flex items-center gap-2 text-white rounded-lg shrink-0 shadow-sm transition-colors ${ontvangstPendingArt ? 'bg-[#059669] hover:bg-[#047857]' : 'bg-[#002FA7] hover:bg-[#00247f]'}`}
                data-testid="ontvangst-toevoegen"
              >
                {ontvangstPendingArt ? <CheckCircle size={18} weight="bold" /> : <Plus size={18} weight="bold" />}
                {ontvangstPendingArt ? 'Bevestig & voeg toe' : 'Toevoegen'}
                <kbd className="hidden md:inline-block ml-1 text-[10px] font-mono font-normal px-1.5 py-0.5 rounded bg-white/15 border border-white/20">⏎</kbd>
              </button>
            </div>

            {/* Pending-art chip — toont wat er gekozen is, wachten op aantal */}
            {ontvangstPendingArt && (
              <div className="mt-3 flex items-center gap-3 bg-green-50 border border-green-300 rounded-lg px-4 py-3" data-testid="ontvangst-pending-chip">
                <CheckCircle size={22} weight="fill" className="text-[#059669] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">Gekozen — typ aantal en druk ENTER</div>
                  <div className="font-mono font-bold text-green-900">{ontvangstPendingArt.art_code}</div>
                  <div className="text-xs text-gray-700 truncate">{ontvangstPendingArt.omschrijving}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase text-gray-500">huidige stock</div>
                  <div className="text-sm font-bold text-gray-900">{Number(ontvangstPendingArt.voorraad || 0).toFixed(2).replace('.', ',')}</div>
                </div>
                <button
                  onClick={() => { setOntvangstPendingArt(null); setOntvangstAantal(1); setTimeout(() => ontvangstScanRef.current?.focus(), 50); }}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                  title="Annuleer keuze (ESC)"
                >
                  <X size={16} />
                </button>
              </div>
            )}

            {/* Workflow in 3 stappen */}
            <div className="mt-3 flex items-center gap-2 flex-wrap text-[11px] text-gray-500" data-testid="ontvangst-workflow">
              {[['1', 'Scan of typ code', 'ENTER'], ['2', 'Typ aantal', 'ENTER'], ['3', 'Regel toegevoegd', null]].map(([n, t, k], i) => (
                <React.Fragment key={n}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-4 h-4 rounded-full bg-[#002FA7] text-white text-[9px] font-bold inline-flex items-center justify-center">{n}</span>
                    <span>{t}</span>
                    {k && <kbd className="font-mono text-[9px] px-1 py-px rounded border border-gray-300 bg-gray-50 text-gray-600">{k}</kbd>}
                  </span>
                  {i < 2 && <span className="text-gray-300">→</span>}
                </React.Fragment>
              ))}
              <span className="ml-auto">Onbekende code? Het nieuw-artikel-formulier verschijnt hieronder.</span>
            </div>
            </div>
          </div>

          {/* Laatste 5 verwerkte ontvangsten + volledige historiek op datum (bewerkbaar) */}
          <OntvangstGeschiedenis artikels={huidig?.items || []} refreshKey={ontvangstRefresh} onGewijzigd={load} />

          {/* INLINE Nieuw-artikel-paneel — volledig zichtbaar op scherm i.p.v. modal */}
          {snelArtikel && (
            <div className="bg-white border-2 border-purple-300 rounded shadow-sm" data-testid="snel-artikel-panel">
              <div className="bg-purple-50 border-b-2 border-purple-200 px-4 py-3 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Package size={24} weight="duotone" className="text-purple-700 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-base font-bold text-purple-900">Nieuw artikel aanmaken</div>
                    <div className="text-[11px] text-purple-700">
                      {snelArtikel.ean
                        ? <>EAN <span className="font-mono bg-purple-100 px-1 rounded">{snelArtikel.ean}</span> niet gevonden — vul aan en voeg toe</>
                        : snelArtikel.code
                          ? <>Code <span className="font-mono bg-purple-100 px-1 rounded">{snelArtikel.code}</span> niet gevonden — vul aan en voeg toe</>
                          : <>Vul de gegevens in en voeg het direct toe aan de ontvangst</>}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSnelArtikel(null)}
                  className="p-1.5 rounded hover:bg-white text-purple-900"
                  title="Sluit (artikel wordt niet aangemaakt)"
                  data-testid="snel-art-sluit"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="p-4 space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Artikelcode <span className="text-red-500">*</span></label>
                    <input
                      ref={snelCodeRef}
                      type="text"
                      value={snelForm.art_code}
                      onChange={e => setSnelForm(p => ({ ...p, art_code: e.target.value }))}
                      placeholder="bv. SCHR-4X40"
                      className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                      data-testid="snel-art-code"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">EAN / Barcode</label>
                    <input
                      type="text"
                      value={snelForm.ean}
                      onChange={e => setSnelForm(p => ({ ...p, ean: e.target.value }))}
                      placeholder="13 cijfers (optioneel)"
                      className="w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                      data-testid="snel-art-ean"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Omschrijving <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={snelForm.omschrijving}
                    onChange={e => setSnelForm(p => ({ ...p, omschrijving: e.target.value }))}
                    placeholder="bv. Schroef hout 4×40 mm verzinkt"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                    data-testid="snel-art-omschr"
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Aankoop (€)</label>
                    <input
                      type="number" step="0.01"
                      value={snelForm.aankoopprijs}
                      onChange={e => setSnelForm(p => ({ ...p, aankoopprijs: e.target.value }))}
                      placeholder="0,00"
                      className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                      data-testid="snel-art-ap"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Verkoop (€)</label>
                    <input
                      type="number" step="0.01"
                      value={snelForm.verkoopprijs}
                      onChange={e => setSnelForm(p => ({ ...p, verkoopprijs: e.target.value }))}
                      placeholder="0,00"
                      className="w-full px-3 py-2 text-sm text-right border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                      data-testid="snel-art-vp"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Eenheid</label>
                    <select
                      value={snelForm.eenheid}
                      onChange={e => setSnelForm(p => ({ ...p, eenheid: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                    >
                      <option value="st">stuk</option>
                      <option value="kg">kg</option>
                      <option value="m">m</option>
                      <option value="m2">m²</option>
                      <option value="m3">m³</option>
                      <option value="l">liter</option>
                      <option value="doos">doos</option>
                      <option value="pak">pak</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Categorie</label>
                    <input
                      type="text"
                      value={snelForm.categorie}
                      onChange={e => setSnelForm(p => ({ ...p, categorie: e.target.value }))}
                      placeholder="bv. Schroeven"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:border-purple-600 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100 flex-wrap">
                  <div className="text-[11px] text-gray-500">
                    Het artikel wordt aangemaakt met voorraad 0 en <strong>{parseFloat(snelArtikel.aantal) || 1} stuk(s)</strong> wordt direct toegevoegd aan de ontvangst-lijst.
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setSnelArtikel(null)}
                      disabled={snelBusy}
                      className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Annuleren
                    </button>
                    <button
                      onClick={submitSnelInline}
                      disabled={snelBusy || !snelForm.art_code.trim() || !snelForm.omschrijving.trim()}
                      className="px-4 py-2 text-sm font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded inline-flex items-center gap-1.5 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      data-testid="snel-art-opslaan"
                    >
                      <Plus size={14} weight="bold" /> {snelBusy ? 'Bezig…' : 'Aanmaken & toevoegen'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {ontvangstRegels.length > 0 && (
            <div className="bg-white border border-gray-200 rounded overflow-hidden">
              <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200 flex-wrap gap-3">
                <div>
                  <div className="font-bold text-gray-900">Te verwerken regels ({ontvangstRegels.length})</div>
                  <div className="text-[11px] text-gray-500">
                    Totaal {ontvangstRegels.reduce((s, r) => s + (parseFloat(r.aantal) || 0), 0)} stuks ·
                    Totale waarde {fmt(ontvangstRegels.reduce((s, r) => s + (parseFloat(r.aantal) || 0) * (parseFloat(r.aankoopprijs) || 0), 0))}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex rounded border border-gray-300 overflow-hidden" data-testid="ontvangst-type">
                    {[['leverancier', 'Leverancier', Truck], ['productie', 'Eigen productie', Factory]].map(([v, l, I]) => (
                      <button key={v} type="button" onClick={() => setOntvangstType(v)}
                        className={`px-2.5 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${ontvangstType === v ? 'bg-[#002FA7] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        data-testid={`ontvangst-type-${v}`}><I size={12} /> {l}</button>
                    ))}
                  </div>
                  {ontvangstType === 'leverancier' && (
                  <input
                    type="text"
                    value={ontvangstLev}
                    onChange={e => setOntvangstLev(e.target.value)}
                    placeholder="Leverancier (optioneel)"
                    className="w-44 px-2 py-1.5 text-xs border border-gray-300 rounded"
                    data-testid="ontvangst-leverancier"
                  />
                  )}
                  <input
                    type="text"
                    value={ontvangstNota}
                    onChange={e => setOntvangstNota(e.target.value)}
                    placeholder="Notitie / leverbon-nr (optioneel)"
                    className="w-52 px-2 py-1.5 text-xs border border-gray-300 rounded"
                    data-testid="ontvangst-nota"
                  />
                  <button
                    onClick={() => setOntvangstRegels([])}
                    className="btn btn-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Wissen
                  </button>
                  <button
                    onClick={ontvangstVerwerken}
                    disabled={ontvangstBusy}
                    className="btn btn-sm inline-flex items-center gap-1.5 bg-[#059669] text-white hover:bg-[#047857] disabled:bg-gray-300"
                    data-testid="ontvangst-verwerken"
                  >
                    <CheckCircle size={14} /> {ontvangstBusy ? 'Bezig…' : 'Verwerken'}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th>Art. code</th>
                      <th>EAN</th>
                      <th>Omschrijving</th>
                      <th className="right">Stock voor</th>
                      <th className="right">Aantal</th>
                      <th className="right">Stock na</th>
                      <th className="right">Aankoop</th>
                      <th className="right">Waarde</th>
                      <th className="right" style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ontvangstRegels.map(r => {
                      const aant = parseFloat(r.aantal) || 0;
                      const ap = parseFloat(r.aankoopprijs) || 0;
                      return (
                        <tr key={r.tmp_id} data-testid={`ontvangst-rij-${r.art_code}`}>
                          <td className="font-mono font-bold">
                            {r.art_code}
                            {r.combo_key && (
                              <span className="ml-1 text-[9px] bg-blue-100 text-blue-800 px-1 rounded font-normal">VAR</span>
                            )}
                          </td>
                          <td className="font-mono text-[10px] text-gray-500">{r.ean || '-'}</td>
                          <td>
                            {r.omschrijving}
                            {r.variant_label && (
                              <div className="text-[10px] text-blue-700 font-mono">🎨 {r.variant_label}</div>
                            )}
                          </td>
                          <td className="right text-gray-500">{fmtNum(r.voorraad_voor)}</td>
                          <td className="right">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={fmtDec(r.aantal)}
                              onChange={e => ontvangstUpdRegel(r.tmp_id, 'aantal', e.target.value.replace(',', '.'))}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-xs font-bold focus:border-purple-600 focus:outline-none"
                            />
                          </td>
                          <td className="right font-bold text-[#059669]">{fmtNum(r.voorraad_voor + aant)}</td>
                          <td className="right">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={fmtDec(r.aankoopprijs)}
                              onChange={e => ontvangstUpdRegel(r.tmp_id, 'aankoopprijs', e.target.value.replace(',', '.'))}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-xs focus:border-purple-600 focus:outline-none"
                            />
                          </td>
                          <td className="right font-bold">{fmt(aant * ap)}</td>
                          <td className="right">
                            <button
                              onClick={() => ontvangstDelRegel(r.tmp_id)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="Verwijderen uit lijst"
                            >
                              <Trash size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {ontvangstRegels.length === 0 && (
            <div className="bg-white border border-dashed border-gray-300 rounded p-10 text-center text-gray-400">
              <Truck size={40} className="mx-auto mb-2" />
              <div className="text-sm">Nog geen artikels gescand. Begin met scannen of typen hierboven.</div>
            </div>
          )}
        </div>
      )}

      {tab === 'bestelsuggesties' && (
        <div className="space-y-4">
          {!bestelsugg || bestelsugg.totaal_tekort_artikels === 0 ? (
            <div className="bg-white border border-gray-200 rounded p-10 text-center">
              <CheckCircle size={48} weight="duotone" className="mx-auto mb-3 text-[#059669]" />
              <div className="text-lg font-bold text-gray-900">Alle voorraden in orde</div>
              <div className="text-sm text-gray-500 mt-1">
                Geen artikels onder hun minimum-voorraad. Stel min/max-voorraad in op artikelniveau om hier suggesties te zien.
              </div>
            </div>
          ) : (
            <>
              {/* KPI bestelsuggesties */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-amber-50 border border-amber-200 p-3 rounded">
                  <div className="text-[11px] uppercase tracking-wider text-amber-700">Artikels onder minimum</div>
                  <div className="text-2xl font-bold text-amber-900">{bestelsugg.totaal_tekort_artikels}</div>
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500">Aan te vullen waarde</div>
                  <div className="text-2xl font-bold text-[#002FA7]">{fmt(bestelsugg.totaal_aan_te_vullen_waarde)}</div>
                </div>
                <div className="bg-white border border-gray-200 p-3 rounded">
                  <div className="text-[11px] uppercase tracking-wider text-gray-500">Leveranciers</div>
                  <div className="text-2xl font-bold text-gray-900">{bestelsugg.per_leverancier?.length || 0}</div>
                </div>
              </div>

              {/* Per leverancier */}
              {bestelsugg.per_leverancier?.map(groep => (
                <div key={groep.leverancier_id || '__geen__'} className="bg-white border border-gray-200 rounded overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                      <Truck size={20} weight="duotone" className="text-[#002FA7]" />
                      <div>
                        <div className="font-bold text-gray-900">{groep.leverancier_naam}</div>
                        <div className="text-[11px] text-gray-500">
                          {groep.artikels.length} artikel(s) · totaal {fmt(groep.totaal_waarde)} excl. BTW
                        </div>
                      </div>
                    </div>
                    {groep.leverancier_id ? (
                      <button
                        onClick={() => maakBestelbon(groep)}
                        className="btn btn-sm inline-flex items-center gap-1.5 bg-[#002FA7] text-white hover:bg-[#1E40AF]"
                        data-testid={`bestelbon-${groep.leverancier_id}`}
                      >
                        <ShoppingCart size={14} /> Bestelbon aanmaken
                      </button>
                    ) : (
                      <span className="text-[11px] text-gray-400 italic">Geen leverancier toegewezen</span>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="data-table text-xs">
                      <thead>
                        <tr>
                          <th>Art. code</th>
                          <th>Omschrijving</th>
                          <th className="right">Stock</th>
                          <th className="right">Min</th>
                          <th className="right">Tekort</th>
                          <th className="right">Bestellen</th>
                          <th className="right">Aankoop</th>
                          <th className="right">Waarde</th>
                        </tr>
                      </thead>
                      <tbody>
                        {groep.artikels.map(a => (
                          <tr key={a.id}>
                            <td className="font-mono font-bold">{a.art_code}</td>
                            <td>{a.omschrijving}</td>
                            <td className="right">{fmtNum(a.voorraad)}</td>
                            <td className="right text-gray-500">{fmtNum(a.min_voorraad)}</td>
                            <td className="right text-red-600 font-bold">{fmtNum(a.tekort)}</td>
                            <td className="right text-[#059669] font-bold">{a.bij_te_bestellen}</td>
                            <td className="right text-gray-500">{fmt(a.aankoopprijs)}</td>
                            <td className="right font-bold">{fmt(a.waarde)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {tab === 'momentopnames' && (
        <div className="bg-white border border-gray-200 rounded">
          {snapshots.length === 0 ? (
            <div className="p-10 text-center text-gray-400">
              <Archive size={40} className="mx-auto mb-2" />
              <div className="text-sm">Nog geen momentopnames gemaakt.</div>
              <div className="text-[11px] mt-1">Maak er een aan om de huidige stock-stand te archiveren.</div>
            </div>
          ) : (
            <table className="data-table text-xs">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Naam</th>
                  <th>Boekjaar</th>
                  <th>Artikels</th>
                  <th className="right">Waarde aankoop</th>
                  <th className="right">Waarde verkoop</th>
                  <th>Gemaakt door</th>
                  <th className="right">Acties</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map(s => (
                  <tr key={s.id} data-testid={`snap-row-${s.id}`}>
                    <td className="font-mono text-[10px]">{fmtDate(s.aangemaakt_at)}</td>
                    <td className="font-bold">{s.naam}</td>
                    <td><span className="px-2 py-0.5 bg-gray-100 rounded text-[10px]">{s.boekjaar}</span></td>
                    <td>{s.totaal_artikels}</td>
                    <td className="right font-bold text-[#002FA7]">{fmt(s.totaal_waarde_aankoop)}</td>
                    <td className="right text-[#059669]">{fmt(s.totaal_waarde_verkoop)}</td>
                    <td className="text-gray-500">{s.aangemaakt_door}</td>
                    <td className="right">
                      <button
                        onClick={() => downloadExcel(s.id)}
                        className="p-1 text-[#059669] hover:bg-green-50 rounded transition-colors mr-1"
                        title="Download Excel"
                        data-testid={`snap-dl-${s.id}`}
                      >
                        <FileXls size={14} />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => delSnapshot(s.id, s.naam)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Verwijderen"
                          data-testid={`snap-del-${s.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {loading && <div className="text-sm text-gray-400 mt-4">Laden...</div>}

      {/* Snel-artikel modal — alleen tonen wanneer NIET op ontvangst tab (anders inline panel) */}
      {tab !== 'ontvangst' && (
        <SnelArtikelModal
          open={!!snelArtikel}
          initial={snelArtikel}
          onClose={() => setSnelArtikel(null)}
          onSave={snelArtikelOpslaan}
        />
      )}

      {/* Variant picker modal — voor ontvangst van artikels met varianten */}
      <VariantPickerModal
        open={!!variantPickerArt}
        artikel={variantPickerArt?.artikel}
        prijsGroep="basic"
        preselectVariant={variantPickerArt?.preselectVariant || null}
        allowZeroStock={true}
        actieLabel="Voeg toe aan ontvangst"
        onClose={() => setVariantPickerArt(null)}
        onSelect={(variant) => {
          const art = variantPickerArt.artikel;
          const aantal = pf(ontvangstAantal) || 1;
          ontvangstAddArt(art, aantal, variant);
          toast.success(`${variant.art_code || art.art_code} toegevoegd`, {
            description: `${aantal} stuks · ${Object.values(variant.combo || {}).join(' / ')}`,
          });
        }}
      />

      {/* Historiek modal — toont alle stock-bewegingen van 1 artikel */}
      {historiekArt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setHistoriekArt(null)}
          data-testid="historiek-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-blue-50 border-b-2 border-blue-200 px-5 py-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <ClockCounterClockwise size={28} weight="duotone" className="text-[#002FA7] shrink-0" />
                <div className="min-w-0">
                  <div className="text-lg font-bold text-blue-900 truncate">
                    Stock-historiek · {historiekArt.artikel?.art_code}
                  </div>
                  <div className="text-xs text-blue-700 truncate">
                    {historiekArt.artikel?.omschrijving} · huidige voorraad: <strong>{fmtNum(historiekArt.artikel?.voorraad)}</strong>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setHistoriekArt(null)}
                className="p-1 rounded hover:bg-white/40 text-blue-900"
                title="Sluiten"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              {historiekArt.bewegingen?.length === 0 ? (
                <div className="p-10 text-center text-gray-400">
                  <ClockCounterClockwise size={40} className="mx-auto mb-2" />
                  <div className="text-sm">Nog geen stock-bewegingen geregistreerd voor dit artikel.</div>
                </div>
              ) : (
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th>Datum</th>
                      <th>Bron</th>
                      <th>Notitie</th>
                      <th className="right">Voor</th>
                      <th className="right">Wijziging</th>
                      <th className="right">Na</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historiekArt.bewegingen.map((b, i) => {
                      const delta = Number(b.delta || 0);
                      const isPositief = delta > 0;
                      return (
                        <tr key={i}>
                          <td className="font-mono text-[10px] whitespace-nowrap">
                            {b.datum ? new Date(b.datum).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' }) : '-'}
                          </td>
                          <td>
                            <span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px] font-mono">
                              {b.bron_type || '-'}
                            </span>
                            {b.bron_id && <span className="ml-1 text-[10px] text-gray-500 font-mono">{b.bron_id}</span>}
                          </td>
                          <td className="text-gray-600 text-[10px]">{b.notitie || '-'}</td>
                          <td className="right text-gray-500">{fmtNum(b.oude_stand)}</td>
                          <td className={`right font-bold ${isPositief ? 'text-[#059669]' : 'text-[#DC2626]'}`}>
                            {isPositief ? '+' : ''}{fmtNum(delta)}
                          </td>
                          <td className="right font-bold">{fmtNum(b.nieuwe_stand)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            <div className="bg-gray-50 border-t border-gray-200 px-5 py-2 text-[11px] text-gray-500">
              {historiekArt.aantal} bewegingen — laatste {Math.min(historiekArt.aantal || 0, 200)} getoond
            </div>
          </div>
        </div>
      )}

      {/* Reset-stock modal */}
      {resetOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => { if (!resetBusy && !resetResult) setResetOpen(false); }}
          data-testid="reset-stock-modal"
        >
          <div
            className="bg-white rounded-lg shadow-2xl w-full max-w-md overflow-hidden border-2 border-red-200"
            onClick={(e) => e.stopPropagation()}
          >
            {!resetResult ? (
              <>
                <div className="bg-red-50 border-b-2 border-red-200 px-5 py-4 flex items-center gap-3">
                  <WarningIcon size={28} weight="fill" className="text-red-600 shrink-0" />
                  <div>
                    <div className="text-lg font-bold text-red-900">Alle stock resetten naar 0</div>
                    <div className="text-xs text-red-700">Deze actie kan niet ongedaan gemaakt worden.</div>
                  </div>
                </div>
                <div className="px-5 py-4 space-y-3 text-sm">
                  <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900">
                    <div className="font-semibold mb-1 flex items-center gap-1.5">
                      <Camera size={14} /> Automatische back-up
                    </div>
                    <p className="text-xs leading-relaxed">
                      Er wordt <strong>automatisch een momentopname</strong> gemaakt van de huidige stock-stand voor de reset. Je kan die later altijd raadplegen onder "Momentopnames".
                    </p>
                  </div>
                  <div className="text-gray-700">
                    Alle <strong>{huidig?.totaal_artikels || 0} artikels</strong> worden op <strong>0</strong> gezet.
                    Gebruik dit bij een jaarlijkse heropname wanneer je met een lege lei wilt beginnen.
                  </div>
                  <div className="pt-1">
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Typ <span className="font-mono bg-red-100 text-red-700 px-1.5 py-0.5 rounded">RESET</span> om te bevestigen
                    </label>
                    <input
                      type="text"
                      autoFocus
                      value={resetInput}
                      onChange={(e) => setResetInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && resetInput === 'RESET' && !resetBusy) doResetStock();
                        if (e.key === 'Escape' && !resetBusy) setResetOpen(false);
                      }}
                      placeholder="RESET"
                      disabled={resetBusy}
                      className={`w-full px-3 py-2 text-base font-mono border-2 rounded focus:outline-none ${
                        resetInput === 'RESET'
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-300 focus:border-red-400'
                      }`}
                      data-testid="reset-stock-input"
                    />
                  </div>
                </div>
                <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setResetOpen(false)}
                    disabled={resetBusy}
                    className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    data-testid="reset-stock-cancel"
                  >
                    Annuleren
                  </button>
                  <button
                    onClick={doResetStock}
                    disabled={resetInput !== 'RESET' || resetBusy}
                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    data-testid="reset-stock-confirm"
                  >
                    {resetBusy ? (
                      <>
                        <ArrowCounterClockwise size={14} className="animate-spin" /> Bezig...
                      </>
                    ) : (
                      <>
                        <ArrowCounterClockwise size={14} /> Stock resetten
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="bg-green-50 border-b-2 border-green-200 px-5 py-4 flex items-center gap-3">
                  <CheckCircle size={28} weight="fill" className="text-green-600 shrink-0" />
                  <div>
                    <div className="text-lg font-bold text-green-900">Stock gereset</div>
                    <div className="text-xs text-green-700">Je kan nu starten met een verse voorraad-telling.</div>
                  </div>
                </div>
                <div className="px-5 py-4 space-y-2 text-sm">
                  <div className="flex items-center justify-between py-1.5 border-b border-gray-100">
                    <span className="text-gray-600">Artikels op 0 gezet</span>
                    <span className="font-bold text-gray-900">{resetResult.artikels_gereset}</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-gray-600">Back-up naam</span>
                    <span className="font-mono text-xs text-gray-800">{resetResult.backup_naam}</span>
                  </div>
                  <div className="text-xs text-gray-500 pt-2">
                    De back-up is terug te vinden onder het tabblad "Momentopnames".
                  </div>
                </div>
                <div className="bg-gray-50 border-t border-gray-200 px-5 py-3 flex items-center justify-end">
                  <button
                    onClick={() => { setResetOpen(false); setResetResult(null); }}
                    className="px-4 py-2 text-sm font-semibold text-white bg-[#002FA7] rounded hover:bg-[#1E40AF]"
                    data-testid="reset-stock-close"
                  >
                    Sluiten
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* Sticky onderbalk: ALTIJD zichtbaar met onbewaarde tellingen + grote CTA */}
      <BarcodeScanner
        open={!!cameraScan}
        onClose={cameraOnClose}
        onDetect={cameraOnDetect}
        title={cameraScan === 'telling' ? 'Telling — scan barcodes (elke scan = +1)' : 'Goederen ontvangen — scan barcode'}
        continuous={cameraScan === 'telling'}
      />

      {gewijzigd > 0 && tab === 'telling' && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 bg-amber-50 border-t-2 border-amber-400 shadow-2xl px-4 py-3"
          data-testid="inventaris-sticky-save-bar"
        >
          <div className="max-w-screen-2xl mx-auto flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-amber-900">
              <Warning size={22} weight="fill" className="text-amber-600" />
              <div>
                <div className="text-sm font-bold leading-tight">
                  {gewijzigd} onbewaarde {gewijzigd === 1 ? 'telling' : 'tellingen'} — niet vergeten op te slaan!
                </div>
                <div className="text-xs leading-tight">
                  Nieuwe waarde na opslaan: <span className="font-bold">{fmt(nieuweWaarde.ap)}</span> (aankoop) ·{' '}
                  <span className="font-bold">{fmt(nieuweWaarde.vp)}</span> (verkoop)
                </div>
              </div>
            </div>
            <div className="flex-1" />
            <button
              className="inline-flex items-center gap-2 bg-white border border-amber-400 text-amber-800 hover:bg-amber-100 px-3 py-2 rounded text-sm font-semibold"
              onClick={() => setTellingen({})}
              data-testid="inventaris-sticky-cancel"
            >
              <X size={16} /> Annuleer wijzigingen
            </button>
            <button
              className="inline-flex items-center gap-2 bg-[#059669] hover:bg-[#047857] text-white px-6 py-3 rounded text-base font-bold shadow-lg disabled:opacity-50 animate-pulse"
              disabled={saving}
              onClick={saveTellingen}
              data-testid="inventaris-sticky-save"
            >
              <FloppyDisk size={20} weight="fill" />
              {saving ? 'Bezig met opslaan…' : `Bewaar ${gewijzigd} ${gewijzigd === 1 ? 'telling' : 'tellingen'} naar database`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
